<template>
  <v-dialog
    :value="dialog"
    persistent
    :retain-focus="false"
    fullscreen
    @keydown.esc="escape(closeDialog)"
  >
    <v-card>
      <v-stepper v-model="stepper" class="elevation-0">
        <v-toolbar
          color="primaryBgr"
          dark
        >
          <v-row>
            <v-col cols="6" md="4">
              <div class="d-flex align-center p-h-100">
                <p-btn-icon
                  type="close"
                  btn-class="ml-n3 mr-5"
                  tabindex="4"
                  @click="escape(closeDialog)"
                ></p-btn-icon>
                <v-toolbar-title>{{ $t('schedule.createSchedule') }}</v-toolbar-title>
              </div>
            </v-col>
            <v-col cols="6" md="4">
              <v-stepper-header class="elevation-0 p-dialog-steps-header theme--light">
                <v-stepper-step step="1">{{ $t('schedule.edit.step1') }}</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="2">{{ $t('schedule.edit.step2') }}</v-stepper-step>
              </v-stepper-header>
            </v-col>
          </v-row>
        </v-toolbar>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-form ref="form" @submit.stop.prevent>
              <v-container>
                <v-row justify="center">
                  <v-col class="p-schedule-create__col mod-left" cols="12" sm="6" md="4" lg="3">
                    <v-text-field
                      ref="title"
                      type="text"
                      v-model.trim="schedule.title"
                      :label="$t('schedule.edit.title')"
                      :rules="rules.title"
                      prepend-icon="mdi-card-text-outline"
                      tabindex="1"
                      clearable
                      autofocus
                      @input="onTitleInput"
                    ></v-text-field>

                    <v-autocomplete
                      v-model="schedule.scheduleTemplateId"
                      :items="scheduleTemplates"
                      :rules="rules.scheduleTemplateId"
                      :label="$t('schedule.edit.template')"
                      :return-object="false"
                      item-value="id"
                      item-text="name"
                      prepend-icon="mdi-file-document-outline"
                      tabindex="1"
                      clearable
                      @change="onTemplateSelected"
                    ></v-autocomplete>

                    <v-checkbox
                      v-model="schedule.includeNote"
                      :label="$t('schedule.edit.includeNote')"
                      class="mt-3"
                      tabindex="1"
                      hide-details
                      dense
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" lg="3" class="text-left">
                    <div class="p-schedule-create__block-title">
                      <span>{{ $t('schedule.edit.member') }}</span>
                      <v-checkbox
                        v-model="membersAll"
                        :label="$t('schedule.edit.all')"
                        tabindex="1"
                        dense
                        @change="onMembersCheckboxChange"
                      ></v-checkbox>
                    </div>
                    <v-divider></v-divider>
                    <div class="p-schedule-create__checkbox-list">
                      <div v-for="(member, index) in projectMembers"
                           :key="member.contactId ? `member-${member.contactId}` : `member-index-${index}`"
                           class="p-schedule-create__checkbox-list-item">
                        <v-checkbox
                          :value="member.contactId"
                          v-model="members"
                          :label="member.contact.displayNameWithCompany"
                          tabindex="1"
                          dense
                        ></v-checkbox>
                      </div>
                    </div>
                    <div class="p-schedule-create__block-title mod-last">
                      <span>{{ $t('schedule.edit.events') }}</span>
                      <v-checkbox
                        v-model="eventsAll"
                        :label="$t('schedule.edit.all')"
                        tabindex="1"
                        dense
                        @change="onEventsCheckboxChange"
                      ></v-checkbox>
                    </div>
                    <v-divider></v-divider>
                    <div class="p-schedule-create__checkbox-list">
                      <div v-for="event in eventCategories"
                           :key="`event-${event.id}`"
                           class="p-schedule-create__checkbox-list-item">
                        <v-checkbox
                          :value="event.id"
                          v-model="events"
                          :label="event.name"
                          tabindex="1"
                          dense
                        ></v-checkbox>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" class="text-center">
                    <v-btn
                      class="p-btn-rounded mt-8"
                      color="primary"
                      tabindex="1"
                      large
                      rounded
                      dark
                      @click="gotoStep2"
                    >
                      {{ $t('next') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-container>
              <v-row>
                <v-col>
                  <p-loading-sheet :overlay="editorOverlay">
                    <ejs-documenteditorcontainer
                      v-if="ejsReady"
                      ref="editor"
                      :enableToolbar="true"
                      :enableSearch='true'
                      :serviceUrl="editorServiceUrl"
                      :documentName="schedule.title"
                      :toolbarItems="editorToolbarItems"
                      height="700px"
                      @toolbarClick="editorToolbarClick"
                      @created="onEditorCreated"
                      @contentChange="onEditorContentChange"
                    ></ejs-documenteditorcontainer>
                  </p-loading-sheet>
                  <div class="d-flex mt-8 justify-center">
                    <v-btn
                      color="primary"
                      class="p-btn-text mx-4"
                      tabindex="3"
                      text
                      large
                      @click="gotoStep1"
                    >
                      {{ $t('back') }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="p-btn-rounded mx-4"
                      tabindex="2"
                      large
                      rounded
                      dark
                      @click="submit"
                    >
                      {{ $t('save') }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>

        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { DocumentEditorContainerPlugin, Toolbar, Search } from '@syncfusion/ej2-vue-documenteditor'

import PBtnIcon from '@/components/PBtnIcon'
import PLoadingSheet from '@/components/PLoadingSheet'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar, Search]
  },


  components: {
    PBtnIcon,
    PLoadingSheet
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      editorOverlay: false,
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      stepper: 1,
      placeholders: {},
      templateContactInputs: [],
      schedule: {},
      events: [],
      members: [],
      eventsAll: false,
      membersAll: false,
      documentEditor: null,
      ejsReady: false,
      generatedBody: '',
      scheduleId: null,
      fullSchedule: {}
    }
  },


  computed: {
    ...mapGetters('scheduleTemplate', ['scheduleTemplateById', 'scheduleTemplates']),
    ...mapGetters('contact', ['contactById', 'shortContactById', 'shortContactsForProject']),
    ...mapGetters('event', ['eventDetailsById', 'eventsByProject']),
    ...mapGetters('member', ['membersByProject', 'memberById']),
    ...mapGetters('project', ['shortProjectById', 'currentProjectId']),
    ...mapGetters('user', ['userShortById']),
    ...mapGetters('eventCategory', ['eventCategories']),

    projectMembers () {
      return this.membersByProject(this.currentProjectId)
    },

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)

      return ['New', 'Open', 'Separator', docxItem, pdfItem, 'Separator', 'Undo', 'Redo', 'Separator', 'Image', 'Table', 'Hyperlink', 'TableOfContents', 'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break', 'Separator', 'Find']
    },

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    selectedTemplate () {
      if (!this.schedule.scheduleTemplateId) return null
      return this.scheduleTemplateById(this.schedule.scheduleTemplateId)
    },

    rulesToApply () {
      const rule = this.rule

      return {
        scheduleTemplateId: [rule.required()],
        title: [rule.required()],
        inputValue: [rule.required()]
      }
    }
  },


  watch: {
    events (events) {
      if (events.length === this.eventCategories.length && !this.eventsAll) {
        this.eventsAll = true
      }
      if (events.length !== this.eventCategories.length && this.eventsAll) {
        this.eventsAll = false
      }
    },

    members (members) {
      if (members.length === this.projectMembers.length && !this.membersAll) {
        this.membersAll = true
      }
      if (members.length !== this.projectMembers.length && this.membersAll) {
        this.membersAll = false
      }
    }
  },


  methods: {
    ...mapActions('schedule', ['createSchedule', 'getSchedule', 'putSchedule']),
    ...mapActions('scheduleTemplate', ['loadScheduleTemplateById']),


    onEventsCheckboxChange (checked) {
      this.events = []
      if (checked) {
        this.events.push(...this.eventCategories.map(el => el.id))
      }
    },

    onMembersCheckboxChange (checked) {
      this.members = []
      if (checked) {
        this.members.push(...this.projectMembers.map(el => el.contactId))
      }
    },

    onDialogOpen () {
      this.stepper = 1
      this.initData()
      this.initWatchedModel('fullSchedule')
      this.resetValidation()
    },

    updateSchedule () {
      this.fullSchedule = this.getObjectCopy(this.schedule)
      this.fullSchedule.members = this.members
      this.fullSchedule.eventCategories = this.events
      this.fullSchedule.title = this.schedule.title
      this.fullSchedule.body = this.$refs.editor?.ej2Instances?.documentEditor?.serialize()
      this.fullSchedule.projectId = this.currentProjectId
    },

    onEditorContentChange () {
      this.updateSchedule()
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.documentEditor = this.$refs.editor?.ej2Instances?.documentEditor
    },

    removeEditor () {
      this.ejsReady = false
    },

    onEditorCreated () {
      if (!this.$refs?.editor) return
      this.updateSchedule()
      this.initWatchedModel('fullSchedule')
    },

    initData () {
      this.placeholders = {}
      this.templateContactInputs = []
      this.schedule = {
        scheduleTemplateId: null,
        title: null,
        includeNote: false,
        body: '.',
        eventCategories: [],
        members: [],
        id: null
      }
      this.events = []
      this.members = []
    },

    onTitleInput () {
      this.updateSchedule()
    },

    async onTemplateSelected () {
      this.updateSchedule()
      this.placeholders = []
      this.templateContactInputs = []

      try {
        this.showSnackbarProcessing()
        let template = this.scheduleTemplateById(this.schedule.scheduleTemplateId)
        if (!template || !template.template) {
          template = await this.loadScheduleTemplateById(this.schedule.scheduleTemplateId)
        }

        if (template) {
          this.schedule.title = template.name
        }

        this.hideSnackbar()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    },

    async editorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.pdf: {
            await this.saveAsPdf(this.$refs.editor, this.schedule.title || 'schedule')
            break
          }
          case this.toolbarItemId.docx: {
            await this.saveAsDocx(this.$refs.editor, this.schedule.title || 'schedule')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    gotoStep1 () {
      this.stepper = 1
    },

    async generateScheduleBody () {
      this.showSnackbarProcessing()
      this.updateSchedule()
      delete this.fullSchedule.body
      const res = await this.createSchedule(this.fullSchedule)
      this.fullSchedule.id = this.schedule.id = res.id
      this.fullSchedule.body = res.body
      this.showSnackbarSuccess(this.$t('schedule.scheduleCreated'))
    },

    async gotoStep2 () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return
      this.stepper = 2
      await this.prepareEditor()

      try {
        this.showSnackbarProcessing(this.$t('schedule.edit.opening'))
        this.editorOverlay = true
        await this.generateScheduleBody()
        this.documentEditor.open(this.fullSchedule.body)
        this.editorOverlay = false
        this.hideSnackbar()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    prepareScheduleForPut () {
      this.preparedSchedule = {}
      this.preparedSchedule.id = this.fullSchedule.id
      this.preparedSchedule.projectId = this.fullSchedule.projectId
      this.preparedSchedule.title = this.fullSchedule.title
      this.preparedSchedule.body = this.fullSchedule.body
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        this.updateSchedule()
        this.prepareScheduleForPut()
        await this.putSchedule(this.preparedSchedule)
        this.showSnackbarSuccess(this.$t('schedule.scheduleCreated'))
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
